import { union } from 'lodash';

/** A factory id as this screen holds it — a Mongo id already serialised to a string. */
export type FactoryId = string;

/**
 * A factory id exactly as it can arrive from the API: normally an already-serialised string, but
 * a populated / non-serialised document reaches the client as an object whose `toString()` yields
 * the same id, which is what the dropdown matches on.
 */
export interface SerialisableId {
  toString(): string;
}

/**
 * A factory id list off the product payload. The API omits the field on products that have never
 * had a factory, and legacy rows can carry blank entries, so both are part of the contract.
 */
export type FactoryIdPayload = Array<FactoryId | SerialisableId | null | undefined> | null | undefined;

/**
 * Normalises a raw id list from the API into plain string ids, dropping empty entries.
 * @param ids Raw value off the product payload.
 * @returns String ids, in source order.
 */
const toIdList = (ids: FactoryIdPayload): FactoryId[] => {
  if (!Array.isArray(ids)) {
    return [];
  }
  return ids.filter((id): id is FactoryId | SerialisableId => id !== null && id !== undefined && id !== '').map((id) => String(id));
};

/**
 * Folds the factories a product already consumes downstream into its own factory selection.
 *
 * `orderFactoryId` (GET /development-orders/:id, built by `getFactoriesInUseForProduct`) lists
 * every factory reached through a Purchase Order, a Product Costing row, a Sample, or a Critical
 * Path row (PO-based or not) — the widest of the three in-use lists, and the only one with no
 * prior lock at all before BM-9761. Merging on hydrate keeps `selectedSupplierFactories` a
 * superset of `orderFactoryId`, which the Factory(s) dropdown's lock and the save-payload guard
 * both assume.
 *
 * `retrieveDevelopmentOrderById` merges the same two lists before it responds, so this is a guard
 * rather than the primary fix — it keeps the screen correct against an older API build.
 *
 * @param savedFactories The product's own `selectedSupplierFactories` array.
 * @param inUseFactories The `orderFactoryId` array returned alongside it.
 * @returns De-duplicated string ids — saved selection first, downstream-only factories appended.
 */
export const mergeInUseFactories = (savedFactories: FactoryIdPayload, inUseFactories: FactoryIdPayload): FactoryId[] =>
  union(toIdList(savedFactories), toIdList(inUseFactories));
